import Utils from "context/utils";
import FreeFormConverter from "../free_form/converter";

export default class GridConverter {
  constructor(shellInterface) {
    this.shellInterface = shellInterface;
  }

  /**
   * Conversion
   */

  blobhubDefinitionToReactFlow(definition, manifest) {
    const gridNodes = [];
    const edges = definition["connections"] || [];

    for (const component of (definition["components"] || [])) {
      const position = component["position"] || {};
      const gridNode = {
        type: component["type"],
        id: component["id"],
        data: {
          component: Utils.deepCopy(component),
          manifest: manifest["components_index"][component["type"]],
          shellInterface: this.shellInterface,
          layoutType: "grid"
        },
        layout: {
          i: component["id"],
          x: position.x || 0,
          y: position.y || 0,
          w: position.width || 1,
          h: position.height || 1,
          ...(position.min_width != null && { minW: position.min_width }),
          ...(position.max_width != null && { maxW: position.max_width }),
          ...(position.min_height != null && { minH: position.min_height }),
          ...(position.max_height != null && { maxH: position.max_height }),
        }
      };

      // Stub manifest when processor of a specified type is no longer supported
      if (!gridNode.data.manifest) {
        gridNode.data.component.type = "no.longer.exists";
      }

      gridNodes.push(gridNode);
    }

    return [gridNodes, edges];
  }

  reactFlowToBlobhubDefinition(gridNodes, edges) {
    const definition = {
      "type": "playground_definition",
      "version": "1.1.0",
      "components": [],
      "connections": edges || [],
    };

    for (const gridNode of (gridNodes || [])) {
      const component = Utils.deepCopy(gridNode.data.component);
      const layout = gridNode.layout || {};
      component.position = {
        x: layout.x,
        y: layout.y,
        width: layout.w,
        height: layout.h,
        ...(layout.minW != null && { min_width: layout.minW }),
        ...(layout.minH != null && { min_height: layout.minH }),
        ...(layout.maxW != null && { max_width: layout.maxW }),
        ...(layout.maxH != null && { max_height: layout.maxH }),
      };
      definition["components"].push(component);
    }

    return definition;
  }

  /**
   * Creation
   */

  createNewNode(componentDefinition, layoutItem, manifest) {
    const skippedRoutes = new Set(["width", "height"]);

    const component = {
      id: Utils.generateUniqueId(),
      category: componentDefinition["category"],
      type: componentDefinition["type"],
      name: componentDefinition["label"],
      ports: []
    };

    // Initialize data input port values, skipping width/height ports (free-form-only)
    for (const portManifest of componentDefinition["ports"]) {
      if ("data_input" === portManifest["category"] && !skippedRoutes.has(portManifest["route"])) {
        const port = {
          "category": "data_input",
          "route": portManifest["route"]
        };
        FreeFormConverter.initPortValue(port, portManifest);
        component.ports.push(port);
      }
    }

    const gridNode = {
      type: component.type,
      id: component.id,
      data: {
        component: component,
        manifest: componentDefinition,
        shellInterface: this.shellInterface,
        layoutType: "grid"
      },
      layout: {
        i: component.id,
        x: layoutItem.x || 0,
        y: layoutItem.y || 0,
        w: layoutItem.w || 1,
        h: layoutItem.h || 1,
      }
    };

    return gridNode;
  }

  /**
   * Sizing
   */

  getComponentGridSize(_componentDefinition, _manifest, gridGeometry) {
    // The manifest pixel defaults are tuned for the free-form ReactFlow
    // canvas and don't translate sensibly to grid units (e.g. a 500px
    // height with row_height=30 produces a 17-row widget). Use a fixed
    // sensible default; users can resize after drop.
    const { columns } = gridGeometry;
    const w = Math.min(4, columns || 4);
    const h = 4;
    return { w, h };
  }

  /**
   * Modification
   */

  doReactFlowChangesImpactDefinition(_changes) {
    // For grid, treat any layout change as impactful — the grid's onLayoutChange is the source of truth.
    return true;
  }

  /**
   * Static helpers — delegated to FreeFormConverter so grid components (which still have ports)
   * benefit from the same logic without duplicating code.
   */

  static parseManifest(manifest) {
    return FreeFormConverter.parseManifest(manifest);
  }

  static initPortValue(port, portManifest) {
    return FreeFormConverter.initPortValue(port, portManifest);
  }

  static getValueData(value) {
    return FreeFormConverter.getValueData(value);
  }

  static getPortValueData(port) {
    return FreeFormConverter.getPortValueData(port);
  }

  static findComponentPort(component, route) {
    return FreeFormConverter.findComponentPort(component, route);
  }

  static getComponentPortValueData(component, route) {
    return FreeFormConverter.getComponentPortValueData(component, route);
  }

  /**
   * Internal helper — builds a fake component with default port values from a manifest entry,
   * used by getComponentGridSize to read manifest pixel defaults without a real component.
   */
  static buildDefaultPorts(componentDefinition) {
    const ports = [];
    for (const portManifest of (componentDefinition["ports"] || [])) {
      if ("data_input" === portManifest["category"]) {
        const port = { "category": "data_input", "route": portManifest["route"] };
        FreeFormConverter.initPortValue(port, portManifest);
        ports.push(port);
      }
    }
    return ports;
  }
}
